/**
 * ¿Está abierto el chat de soporte?
 *
 * De esta pregunta cuelgan dos cosas visibles: en móvil hay que DESACTIVAR el
 * desplazamiento de la burbuja cuando el panel se abre a pantalla completa (si no,
 * el panel entero sube 90px y deja una franja en blanco abajo), y el recorrido
 * guiado tiene que apartarse mientras dure la conversación.
 *
 * Crisp lo contaba con eventos (`chat:opened` / `chat:closed`). No todos los
 * proveedores los ofrecen, así que aquí se deduce del DOM. La idea es no depender
 * de nombres de clase internos —que van ofuscados y cambian sin avisar— sino de
 * dos propiedades físicas que cualquier widget tiene que cumplir para que se le
 * vea: hay una caja GRANDE y esa caja se está PINTANDO.
 *
 * Las dos condiciones, no una. Medido sobre el DOM real de Crisp:
 *
 * - Solo el tamaño no vale: con el chat cerrado su panel sigue montado y midiendo
 *   400x730. Lo único que cambia al abrirlo es la `visibility`.
 * - Solo la visibilidad tampoco: sus contenedores intermedios son bandas de ancho
 *   completo visibles siempre, y una de ellas mide 1905px de ancho.
 *
 * Efecto lateral útil: mide la caja del elemento, así que funciona igual si el
 * panel es un <iframe> cross-origin, cuyo contenido no podríamos inspeccionar.
 */

// Holgura amplia a propósito. La burbuja de Crisp mide 54px; la de cualquier otro
// proveedor anda por ahí. Su panel abierto mide 343x414 en escritorio y el
// viewport entero en móvil. Entre 54 y 343 no hay nada, así que el umbral no
// necesita ser fino.
export const OPEN_MIN_HEIGHT = 200;
export const OPEN_MIN_WIDTH = 260;

/**
 * Función pura, y la única parte de este fichero que se puede testear en jsdom
 * (`src/test/setup.js` stubbea `ResizeObserver` con no-ops, así que el cableado
 * de observadores no dispara nada ahí y se valida a mano en el navegador).
 *
 * Exige que UNA MISMA caja pase los DOS umbrales, y no que se pasen entre varias.
 * Medido sobre el DOM real de Crisp: sus contenedores intermedios son bandas de
 * ancho completo y altura cero (1905x0 en un portátil), tan anchas abierto como
 * cerrado. Con un `o` en vez de un `y`, esas bandas darían "abierto" siempre y el
 * CSS de posición se desactivaría solo.
 *
 * Espera recibir SOLO cajas que se estén pintando de verdad: ver `isRendered`.
 *
 * @param {Array<{ width: number, height: number }>} rects
 */
export function isOpenFromRects(rects) {
  for (const r of rects) {
    if (!r) continue;
    if (r.height > OPEN_MIN_HEIGHT && r.width > OPEN_MIN_WIDTH) return true;
  }
  return false;
}

/**
 * ¿Este elemento se está pintando de verdad?
 *
 * Hace falta porque el tamaño por sí solo no distingue abierto de cerrado.
 * Comprobado sobre Crisp: con el chat cerrado su panel sigue montado Y MEDIDO
 * —catorce cajas de hasta 400x730— y lo único que cambia es la `visibility`.
 * Sin este filtro, el chat constaría como abierto desde el primer render.
 *
 * `checkVisibility` lo resuelve de una y es lo que usan los navegadores al día;
 * el respaldo cubre a los que no lo traen. Se llama solo sobre las cajas que ya
 * pasaron el filtro de tamaño (una docena), no sobre el subárbol entero.
 */
export function isRendered(el) {
  if (typeof el.checkVisibility === 'function') {
    return el.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true });
  }
  const cs = getComputedStyle(el);
  if (cs.display === 'none' || cs.visibility === 'hidden') return false;
  // Crisp tiene un contenedor a 0.06 de opacidad con el chat cerrado. Un panel
  // que se esté enseñando nunca anda por ahí.
  return Number(cs.opacity) > 0.1;
}

/**
 * Observa el widget y avisa cuando cambia el estado abierto/cerrado.
 *
 * @param {{
 *   selector: string,
 *   ignoreRectSelector?: string,
 *   onChange: (open: boolean) => void,
 * }} options
 * @returns {() => void} función para dejar de observar
 */
export function observeOpenState({ selector, ignoreRectSelector = '', onChange }) {
  let stopped = false;
  let current = false;
  let frame = null;
  let settle = null;
  /** Las cajas que se estan observando ahora mismo, con sus observadores. */
  const watched = new Map();

  const evaluate = () => {
    frame = null;
    const roots = Array.from(document.querySelectorAll(selector));
    // Sin ninguna caja no se decide nada y se conserva el ultimo valor. El hueco
    // existe de verdad: al abrir por primera vez, Richpanel borra su cargador
    // antes de colgar el iframe del panel, y dar por cerrado ese instante seria
    // un parpadeo del CSS que depende de esto.
    if (roots.length === 0) return;
    const nodes = [];
    for (const root of roots) {
      // Se mide el SUBARBOL entero, no los hijos directos. Con Crisp, los hijos
      // directos son cuatro bandas de altura cero que no cambian al abrir el
      // panel: el panel cuelga varios niveles por debajo, y quien mirara solo el
      // primer nivel no detectaria nunca una apertura.
      //
      // La caja del propio contenedor entra o no segun `ignoreRectSelector`, que
      // existe por un pez que se muerde la cola: la tecnica que usamos para subir
      // la burbuja en movil convierte los contenedores `static` en cajas a
      // pantalla completa, y medir esas daria "abierto" siempre. Los que ya son
      // `fixed` SI se miden, y en Richpanel es imprescindible: en cuanto el
      // messenger de verdad carga, el panel es un <iframe> sin descendientes que
      // recorrer y su propio tamaño es lo unico que distingue la burbuja
      // (120x120) del panel abierto (450x911).
      if (!ignoreRectSelector || !root.matches(ignoreRectSelector)) nodes.push(root);
      nodes.push(...Array.from(root.querySelectorAll('*')));
    }
    // Primero el tamaño y despues la visibilidad, no al reves: medir es barato
    // y deja una docena de candidatos; consultar estilos calculados sobre el
    // subárbol entero (150 nodos con Crisp) sería tirar el presupuesto.
    const visibleRects = nodes
      .map((n) => ({ node: n, rect: n.getBoundingClientRect() }))
      .filter(({ rect }) => rect.height > OPEN_MIN_HEIGHT && rect.width > OPEN_MIN_WIDTH)
      .filter(({ node }) => isRendered(node))
      .map(({ rect }) => rect);
    const next = isOpenFromRects(visibleRects);
    // Salir sin cambios no es una micro-optimización: quien escucha toca
    // `documentElement.classList`, y notificar en balde por cada mutación del
    // widget convierte una animación de apertura en decenas de escrituras.
    if (next === current) return;
    current = next;
    onChange(next);
  };

  // Se agrupan las mutaciones de una rafaga en una sola medición: abrir el panel
  // dispara decenas seguidas.
  //
  // Dos lecturas y no una. La inmediata es la que hace que el recorrido guiado se
  // aparte sin retardo perceptible; la de reposo existe porque el panel se abre
  // con una transición, y una medida tomada a mitad puede leer todavía la caja
  // pequeña. Si nadie volviera a mirar, ese valor se quedaría clavado: la
  // transición al terminar no dispara ninguna mutación.
  //
  // Con `setTimeout` y no con `requestAnimationFrame`: el segundo no corre en
  // pestañas de fondo, y ahí el estado se quedaría congelado hasta que el usuario
  // volviera. `getBoundingClientRect` fuerza el cálculo de layout por su cuenta,
  // así que no se pierde precisión por medir fuera del frame de pintado.
  const SETTLE_MS = 600;
  const schedule = () => {
    if (stopped) return;
    if (frame === null) frame = setTimeout(evaluate, 0);
    if (settle !== null) clearTimeout(settle);
    settle = setTimeout(() => { settle = null; evaluate(); }, SETTLE_MS);
  };

  const forget = (root) => {
    const entry = watched.get(root);
    if (!entry) return;
    entry.mo.disconnect();
    if (entry.ro) entry.ro.disconnect();
    root.removeEventListener('transitionend', schedule);
    root.removeEventListener('animationend', schedule);
    watched.delete(root);
  };

  const watch = (root) => {
    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      // Cubre "el mismo nodo crece", que es lo que hace el <iframe> del panel.
      ro = new ResizeObserver(schedule);
      ro.observe(root);
      for (const child of root.children) ro.observe(child);
    }
    // Cubre "aparece un nodo nuevo", que es lo que hace un widget que monta y
    // desmonta el panel en vez de redimensionarlo. Sin esto, un `ResizeObserver`
    // sobre el lanzador no dispararia nunca.
    const mo = new MutationObserver(() => {
      if (ro) for (const child of root.children) ro.observe(child);
      schedule();
    });
    // SIN `attributeFilter`. La primera versión filtraba por `style`, `class`,
    // `hidden` y `aria-hidden`, y no detectaba ni una sola apertura: Crisp
    // esconde y enseña su panel con atributos propios (`data-visible`,
    // `data-maximized`), que no estaban en la lista. Adivinar de qué se vale un
    // tercero para ocultar cosas es justo lo que este fichero intenta no hacer,
    // y el filtro era esa apuesta disfrazada de optimización. Las llamadas de más
    // se agrupan en `schedule` y `evaluate` sale sin hacer nada si no ha cambiado
    // el estado, así que lo que ahorraba era ruido.
    mo.observe(root, { childList: true, subtree: true, attributes: true });
    // El cierre es lo que obliga a esto. Abrir se nota enseguida —el panel se
    // hace visible al principio del movimiento— pero al cerrar sigue visible
    // durante toda la animación, y cuando termina no hay ninguna mutación que
    // mirar: sin este enganche el estado se quedaba clavado en "abierto".
    // `transitionend` y `animationend` burbujean, asi que basta escucharlos en la
    // raiz para enterarse de cualquier descendiente.
    root.addEventListener('transitionend', schedule);
    root.addEventListener('animationend', schedule);
    watched.set(root, { ro, mo });
  };

  /**
   * Reengancha los observadores a las cajas que haya AHORA.
   *
   * Es la parte que hay que llamar cada vez que cambian los hijos del `<body>`, y
   * no un lujo: la version anterior se enganchaba UNA vez a la primera caja que
   * encontraba y ahí se quedaba. Como Richpanel borra su cargador la primera vez
   * que se abre el chat y cuelga otro elemento en su sitio, el observador se
   * quedaba mirando un nodo desconectado y el estado se congelaba para siempre en
   * el que tuviera en ese instante — normalmente "abierto", que es justo el que
   * desactiva el CSS que sube la burbuja en movil.
   */
  const sync = () => {
    if (stopped) return;
    const roots = new Set(Array.from(document.querySelectorAll(selector)));
    for (const root of [...watched.keys()]) if (!roots.has(root)) forget(root);
    for (const root of roots) if (!watched.has(root)) watch(root);
    schedule();
  };

  // `subtree: false` a proposito: solo interesa que aparezcan y desaparezcan
  // hijos DIRECTOS del body, que es donde el widget cuelga sus cajas. Un
  // observador de subarbol sobre el body se paga en cada render de cualquier
  // pagina de la SPA.
  const bodyObserver = new MutationObserver(sync);
  bodyObserver.observe(document.body, { childList: true });
  sync();

  return () => {
    stopped = true;
    if (frame !== null) clearTimeout(frame);
    if (settle !== null) clearTimeout(settle);
    bodyObserver.disconnect();
    for (const root of [...watched.keys()]) forget(root);
  };
}
